class BaseActions {
    // Abstract method to be implemented by subclasses
    static getBitsDict() {
        throw new Error('getBitsDict() must be implemented in subclasses');
    }

    /**
     * Calculates a bitmask for a given list of actions.
     * @param {string[]} actions - List of action names.
     * @returns {number} - The resulting bitmask.
     */
    static getMask(actions) {
        let result = 0;

        for (const action of actions) {
            result |= this.getBitsDict()[action] || 0;
        }

        return result;
    }

    /**
     * Retrieves a list of actions from a given bitmask.
     * @param {number} mask - The bitmask to decode.
     * @returns {string[]} - List of action names corresponding to the bitmask.
     */
    static getActions(mask) {
        const result = [];

        for (const [action, bit] of Object.entries(this.getBitsDict())) {
            if (mask & bit) {
                result.push(action);
            }
        }

        return result;
    }
}

class BookActions extends BaseActions {
    static RETRIEVE = 'retrieve';
    static UPDATE_REPLACE_PARTS = 'update_replace_parts';
    static REPLACE_PARTS = 'replace_parts';
    static PAUSE_DURATION = 'pause_duration';
    static REINDEX_EMPTY_PARTS = 'reindex_empty_parts';
    static REMOVE_SAMPLE_FLAGS = 'remove_sample_flags';
    static LINKAGE = 'linkage';
    static MULTIPLE_LINKAGE = 'multiple_linkage';
    static EMPTY_PARTS = 'empty_parts';
    static IN_QUEUE = 'in_queue';
    static BROKEN_PARTS = 'broken_parts';
    static SEARCH = 'search';
    static CONTENTS = 'contents';
    static RANDOM_PARTS = 'random_parts';
    static DELETE = 'delete';
    static SECTIONS_DURATION = 'sections_duration';
    static REPLACE_ALL_PARTS = 'replace_all_parts';
    static REPLACE_SINGLE_PART = 'replace_single_part';
    static CURRENT_TIMING = 'current_timing';
    static ADD_SENTENCES = 'add_sentences';
    static CHARS_INFO = 'chars_info';
    static CHANGE_BOOK_PARTS_SPEAKER = 'change_book_parts_speaker';

    static getBitsDict() {
        return {
            [BookActions.RETRIEVE]: 1 << 0,
            [BookActions.UPDATE_REPLACE_PARTS]: 1 << 1,
            [BookActions.REPLACE_PARTS]: 1 << 2,
            [BookActions.PAUSE_DURATION]: 1 << 3,
            [BookActions.REINDEX_EMPTY_PARTS]: 1 << 4,
            [BookActions.REMOVE_SAMPLE_FLAGS]: 1 << 5,
            [BookActions.LINKAGE]: 1 << 6,
            [BookActions.MULTIPLE_LINKAGE]: 1 << 7,
            [BookActions.EMPTY_PARTS]: 1 << 8,
            [BookActions.IN_QUEUE]: 1 << 9,
            [BookActions.BROKEN_PARTS]: 1 << 10,
            [BookActions.SEARCH]: 1 << 11,
            [BookActions.CONTENTS]: 1 << 12,
            [BookActions.RANDOM_PARTS]: 1 << 13,
            [BookActions.DELETE]: 1 << 14,
            [BookActions.SECTIONS_DURATION]: 1 << 15,
            [BookActions.REPLACE_ALL_PARTS]: 1 << 16,
            [BookActions.REPLACE_SINGLE_PART]: 1 << 17,
            [BookActions.CURRENT_TIMING]: 1 << 18,
            [BookActions.ADD_SENTENCES]: 1 << 19,
            [BookActions.CHARS_INFO]: 1 << 20,
            [BookActions.CHANGE_BOOK_PARTS_SPEAKER]: 1 << 21
        };
    }
}

class BookPartActions extends BaseActions {
    static LIST = 'list';
    static RETRIEVE = 'retrieve';
    static GENERATE = 'generate';
    static DESTROY = 'destroy';
    static TEXT_TYPES = 'text_types';
    static PART_TYPES = 'part_types';
    static GENERATE_TYPES = 'generate_types';
    static ALL_ENUMS = 'all_enums';
    static IN_QUEUE = 'in_queue';
    static REPAIR = 'repair';
    static SWAP_TYPE = 'swap_type';
    static UPDATE = 'update';
    static PARTIAL_UPDATE = 'partial_update';
    static GLUING = 'gluing';
    static SPLIT = 'split';

    static getBitsDict() {
        return {
            [BookPartActions.LIST]: 1 << 0,
            [BookPartActions.RETRIEVE]: 1 << 1,
            [BookPartActions.GENERATE]: 1 << 2,
            [BookPartActions.DESTROY]: 1 << 3,
            [BookPartActions.TEXT_TYPES]: 1 << 4,
            [BookPartActions.PART_TYPES]: 1 << 5,
            [BookPartActions.GENERATE_TYPES]: 1 << 6,
            [BookPartActions.ALL_ENUMS]: 1 << 7,
            [BookPartActions.IN_QUEUE]: 1 << 8,
            [BookPartActions.REPAIR]: 1 << 9,
            [BookPartActions.SWAP_TYPE]: 1 << 10,
            [BookPartActions.UPDATE]: 1 << 11,
            [BookPartActions.PARTIAL_UPDATE]: 1 << 12,
            [BookPartActions.GLUING]: 1 << 13,
            [BookPartActions.SPLIT]: 1 << 14
        };
    }
}

class CorrectionActions extends BaseActions {
    static LIST = 'list';
    static RETRIEVE = 'retrieve';
    static STATES = 'states';
    static CREATE = 'create';
    static DESTROY = 'destroy';
    static CHANGE_STATE = 'change_state';
    static THREAD = 'thread';
    static INFORMATION = 'information';

    static getBitsDict() {
        return {
            [CorrectionActions.LIST]: 1 << 0,
            [CorrectionActions.RETRIEVE]: 1 << 1,
            [CorrectionActions.STATES]: 1 << 2,
            [CorrectionActions.CREATE]: 1 << 3,
            [CorrectionActions.DESTROY]: 1 << 4,
            [CorrectionActions.CHANGE_STATE]: 1 << 5,
            [CorrectionActions.THREAD]: 1 << 6,
            [CorrectionActions.INFORMATION]: 1 << 7
        };
    }
}

class BackgroundAudioActions extends BaseActions {
    static LIST = 'list';
    static RETRIEVE = 'retrieve';
    static AUDIO_TYPES = 'audio_types';

    static getBitsDict() {
        return {
            [BackgroundAudioActions.LIST]: 1 << 0,
            [BackgroundAudioActions.RETRIEVE]: 1 << 1,
            [BackgroundAudioActions.AUDIO_TYPES]: 1 << 2
        };
    }
}

class PrerecordedAudioActions extends BaseActions {
    static LIST = 'list';
    static RETRIEVE = 'retrieve';
    static PAUSES = 'pauses';
    static AUDIO_TYPES = 'audio_types';

    static getBitsDict() {
        return {
            [PrerecordedAudioActions.LIST]: 1 << 0,
            [PrerecordedAudioActions.RETRIEVE]: 1 << 1,
            [PrerecordedAudioActions.PAUSES]: 1 << 2,
            [PrerecordedAudioActions.AUDIO_TYPES]: 1 << 3
        };
    }
}

class PersonageActions extends BaseActions {
    static LIST = 'list';
    static RETRIEVE = 'retrieve';
    static CREATE = 'create';
    static UPDATE = 'update';
    static PARTIAL_UPDATE = 'partial_update';
    static DESTROY = 'destroy';

    static getBitsDict() {
        return {
            [PersonageActions.LIST]: 1 << 0,
            [PersonageActions.RETRIEVE]: 1 << 1,
            [PersonageActions.CREATE]: 1 << 2,
            [PersonageActions.UPDATE]: 1 << 3,
            [PersonageActions.PARTIAL_UPDATE]: 1 << 4,
            [PersonageActions.DESTROY]: 1 << 5
        };
    }
}

class SuggestionActions extends BaseActions {
    static LIST = 'list';
    static RETRIEVE = 'retrieve';
    static UPDATE = 'update';
    static PARTIAL_UPDATE = 'partial_update';

    static getBitsDict() {
        return {
            [SuggestionActions.LIST]: 1 << 0,
            [SuggestionActions.RETRIEVE]: 1 << 1,
            [SuggestionActions.UPDATE]: 1 << 2,
            [SuggestionActions.PARTIAL_UPDATE]: 1 << 3
        };
    }
}

class StyleRuleActions extends BaseActions {
    static LIST = 'list';

    static getBitsDict() {
        return {
            [StyleRuleActions.LIST]: 1 << 0
        };
    }
}

export class CoreEntities {
    static BOOK = 'book';
    static BOOK_PART = 'book_part';
    static CORRECTION = 'correction';
    static BACKGROUND_AUDIO = 'background_audio';
    static PRERECORDED_AUDIO = 'prerecorded_audio';
    static PERSONAGE = 'personage';
    static SUGGESTION = 'suggestion';
    static STYLE_RULE = 'style_rule';

    /**
     * Returns the corresponding actions class for a given entity.
     * @param {string} entity - The entity name.
     * @returns {class} - The corresponding actions class.
     */
    static getActions(entity) {
        const actionsMap = {
            [CoreEntities.BOOK]: BookActions,
            [CoreEntities.BOOK_PART]: BookPartActions,
            [CoreEntities.CORRECTION]: CorrectionActions,
            [CoreEntities.BACKGROUND_AUDIO]: BackgroundAudioActions,
            [CoreEntities.PRERECORDED_AUDIO]: PrerecordedAudioActions,
            [CoreEntities.PERSONAGE]: PersonageActions,
            [CoreEntities.SUGGESTION]: SuggestionActions,
            [CoreEntities.STYLE_RULE]: StyleRuleActions
        };

        return actionsMap[entity];
    }
}
